import React from "react";

/**
 * Pure-SVG sparkline. No dependency. Renders an n-point line chart
 * inside a fixed viewBox so it scales with any parent width.
 *
 * Props:
 *   values    — number[] (nullable entries are skipped, line breaks across them)
 *   color     — stroke colour (default emerald)
 *   filled    — when true, fills the area under the line
 *   testId    — data-testid passthrough
 *
 * Used by the Boardroom KPI cards (P2.1 trend awareness) and the
 * dedicated Trends page.
 */
export default function TrendSparkline({
  values = [],
  color = "#10B981",
  filled = false,
  testId,
  height = 36,
}) {
  // Only consider numeric entries — null / undefined means "no
  // upload for this period". We collapse the line over gaps so
  // sparse data still renders cleanly.
  const points = values
    .map((v, i) => ({ x: i, y: typeof v === "number" ? v : null }))
    .filter((p) => p.y !== null);

  if (points.length < 2) {
    return (
      <svg viewBox="0 0 100 36" className="w-full" height={height}
           data-testid={testId} aria-label="trend-no-data">
        <line x1="0" x2="100" y1="18" y2="18"
              stroke="#E2E8F0" strokeDasharray="3 3" strokeWidth="1" />
      </svg>
    );
  }

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const padX = 2;
  const padY = 4;
  const innerW = 100 - 2 * padX;
  const innerH = 36  - 2 * padY;

  const xRange = Math.max(1, maxX - minX);
  const yRange = Math.max(1, maxY - minY);
  const sx = (x) => padX + ((x - minX) / xRange) * innerW;
  const sy = (y) => padY + (1 - (y - minY) / yRange) * innerH;

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${sx(p.x).toFixed(2)} ${sy(p.y).toFixed(2)}`)
    .join(" ");
  const areaPath = filled
    ? `${linePath} L ${sx(points[points.length - 1].x).toFixed(2)} ${36 - padY} L ${sx(points[0].x).toFixed(2)} ${36 - padY} Z`
    : null;

  // Trend direction colour: dim emerald when up, dim red when down,
  // unless an explicit colour was passed.
  const lastY  = points[points.length - 1].y;
  const firstY = points[0].y;
  const autoColor = lastY >= firstY ? "#10B981" : "#EF4444";
  const stroke = color === "auto" ? autoColor : color;

  return (
    <svg viewBox="0 0 100 36" className="w-full" height={height}
         data-testid={testId} aria-label="trend">
      {filled && (
        <path d={areaPath} fill={stroke} fillOpacity="0.12" />
      )}
      <path d={linePath} fill="none" stroke={stroke}
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots at start + end so a 2-point series is still readable */}
      <circle cx={sx(points[0].x)} cy={sy(points[0].y)} r="1.6" fill={stroke} />
      <circle cx={sx(points[points.length - 1].x)} cy={sy(points[points.length - 1].y)}
              r="1.8" fill={stroke} />
    </svg>
  );
}
